import { useCallback, useMemo, useState } from 'react'
import { FormMessageProps } from '../components/molecules/FormMessage/types'
import { ContactFormData, ContactFormField, ContactFormProps } from '../components/organisms/ContactForm/types'
import { mapButtonProps } from '../components/atoms/Button/utils'
import { resolvePrismicLink } from '../utils/prismicLinkResolver'
import usePageContext from './usePageContext'

const useContactForm = (document?: GatsbyTypes.PrismicContactFormFragment): ContactFormProps => {
  const { action, title, form_title } = document?.data || {}

  const { group } = usePageContext()

  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<Partial<FormMessageProps>>()

  const fields: ContactFormField[] = useMemo(() => {
    const {
      field_first_name_label,
      field_first_name_hidden,
      field_first_name_required,
      field_first_name_validation_message,
      field_last_name_label,
      field_last_name_hidden,
      field_last_name_required,
      field_last_name_validation_message,
      field_company_label,
      field_company_hidden,
      field_company_required,
      field_company_validation_message,
      field_email_label,
      field_email_hidden,
      field_email_required,
      field_email_validation_message,
      field_phone_number_label,
      field_phone_number_hidden,
      field_phone_number_required,
      field_phone_number_validation_message,
      field_country_label,
      field_country_hidden,
      field_country_required,
      field_country_validation_message,
      field_country_options,
      field_message_label,
      field_message_hidden,
      field_message_required,
      field_message_validation_message
    } = document?.data || {}

    return [
      {
        fieldName: 'firstName',
        label: field_first_name_label || '',
        hidden: field_first_name_hidden,
        required: field_first_name_required,
        validationMessage: field_first_name_validation_message
      },
      {
        fieldName: 'lastName',
        label: field_last_name_label || '',
        hidden: field_last_name_hidden,
        required: field_last_name_required,
        validationMessage: field_last_name_validation_message
      },
      {
        fieldName: 'company',
        label: field_company_label || '',
        hidden: field_company_hidden,
        required: field_company_required,
        validationMessage: field_company_validation_message
      },
      {
        fieldName: 'email',
        label: field_email_label || '',
        hidden: field_email_hidden,
        required: field_email_required,
        validationMessage: field_email_validation_message
      },
      {
        fieldName: 'phoneNumber',
        label: field_phone_number_label || '',
        hidden: field_phone_number_hidden,
        required: field_phone_number_required,
        validationMessage: field_phone_number_validation_message
      },
      {
        fieldName: 'country',
        label: field_country_label || '',
        hidden: field_country_hidden,
        required: field_country_required,
        validationMessage: field_country_validation_message,
        items: field_country_options?.split('\n').map((value) => ({ title: value, value }))
      },
      {
        fieldName: 'message',
        label: field_message_label || '',
        hidden: field_message_hidden,
        required: field_message_required,
        validationMessage: field_message_validation_message
      }
    ]
  }, [document?.data])

  const successMessage: Partial<FormMessageProps> = useMemo(() => {
    const {
      success_title,
      success_description,
      success_button_text,
      success_button_type,
      success_button_link,
      success_button_is_centered,
      success_caption,
      success_caption_mobile_size,
      success_caption_desktop_size
    } = document?.data || {}

    return {
      isSuccess: true,
      heading: success_title,
      description: success_description,
      button: mapButtonProps(
        success_button_type,
        success_button_text,
        resolvePrismicLink(success_button_link, group),
        success_button_link?.link_type,
        success_caption,
        success_caption_mobile_size,
        success_caption_desktop_size,
        success_button_is_centered
      )
    }
  }, [document?.data])

  const errorMessage: Partial<FormMessageProps> = useMemo(() => {
    const {
      error_title,
      error_description,
      error_button_text,
      error_button_type,
      error_button_link,
      error_button_is_centered,
      error_caption,
      error_caption_mobile_size,
      error_caption_desktop_size
    } = document?.data || {}

    return {
      isSuccess: false,
      heading: error_title,
      description: error_description,
      button: mapButtonProps(
        error_button_type,
        error_button_text,
        resolvePrismicLink(error_button_link, group),
        error_button_link?.link_type,
        error_caption,
        error_caption_mobile_size,
        error_caption_desktop_size,
        error_button_is_centered
      )
    }
  }, [document?.data])

  const handleSubmit = useCallback(
    async (values: ContactFormData) => {
      if (action?.url) {
        setLoading(true)

        const formData = new FormData()

        formData.append('formTitle', `${form_title} - ${values.email}`)

        Object.entries(values).forEach(([name, value]) => formData.append(name, value))

        let responseJSON: { success: boolean; data: { message: string } } | undefined

        try {
          const response = await fetch(action.url, { method: 'POST', body: formData })
          responseJSON = await response.json()
        } catch {}

        if (responseJSON?.success) {
          setMessage(successMessage)
        } else {
          setMessage(errorMessage)
        }

        setLoading(false)

        return responseJSON
      }
    },
    [action?.url, form_title, successMessage, errorMessage]
  )

  return {
    head: title?.text || '',
    message,
    disabled: loading,
    fields,
    onSubmit: handleSubmit
  }
}

export default useContactForm
